/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { FormInputWrapper } from "@eisinc/MobiusOverrides/FormField";
import { PaginationPresentationProps } from "@eisinc/MobiusOverrides/Pagination";
import { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { ProductDescriptionOptions } from "@eisinc/XDL/types";
import color from "@eisinc/XDL/Utilities/color";
import { mergeWithCss } from "@eisinc/XDL/Utilities/mergeWithCss";
import { ellipsis } from "@eisinc/XDL/Utilities/string";
import xScreen from "@eisinc/XDL/Utilities/xScreen";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}
        padding: 10px 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 3px;
        ${StyledClickable} {
            ${/* sc-selector */ StyledTypography} {
                font-size: 14px;
                padding: 10px 15px;
                color: ${resolveColor("text", theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor("common.background", theme)};
                color: ${resolveColor("text", theme)};
                text-decoration: underline;
            }
        }
    `;
};

export const styledSlider = () => {
    return ({ theme }: any) => css`
        position: relative;
        .swiper-container {
            width: 100% !important;
        }
        .swiper-carousel {
            box-sizing: border-box;
            display: flex;
            justify-content: center;
            z-index: 0;
        }
        .swiper-wrapper {
            box-sizing: border-box;
            z-index: 0;
        }

        .swiper-navigation {
            z-index: 1;
            > div {
                color: ${getColor("secondary")};
                > svg {
                    width: 30px;
                    height: 30px;
                }
            }
            .swiper-button-prev {
                z-index: 0;
                left: -25px;
                @media screen and (max-width: 1024px) {
                    left: -20px;
                }
                @media screen and (max-width: 768px) {
                    left: -5px;
                }
                &:after {
                    content: none;
                }
            }

            .swiper-button-next {
                z-index: 0;
                right: -25px;
                @media screen and (max-width: 1024px) {
                    right: -17px;
                }
                @media screen and (max-width: 768px) {
                    right: -5px;
                }
                &:after {
                    content: none;
                }
            }
        }

        .swiper-pagination-bullet {
            height: 19px;
            width: 19px;
            margin-right: 54px !important;
            &:last-child {
                margin-right: 54px !important;
            }
            background: ${getColor("common.ui3")};
            opacity: 1;
        }
        .swiper-pagination-bullet-active {
            background: ${getColor("primary.main")};
        }

        .swiper-button-disabled {
            display: none;
        }

        .swiper-slide {
            width: 100%;
            padding: 5px 15px;
            height: auto;
            min-height: 100%;

            @media (max-width: 768px) {
                display: flex;
                justify-content: center;
            }

            > div:first-child {
                height: 100%;

                @media (max-width: 768px) {
                    max-width: 310px;
                }
            }
        }
    `;
};

export const hasVScroll = () => document.body.scrollHeight > document.body.clientHeight;

export const showAllTabPagination = {
    cssOverrides: {
        perPageSelect: css`
            ${FormInputWrapper} {
                width: auto;
                ${IconWrapper} {
                    right: 0;
                }
            }
        `,
        pagination: css`
            gap: 0px 48px;
        `,
    },
} as PaginationPresentationProps;

const linkSizeVariants = {
    small: {
        size: 12,
        lineHeight: "14px",
        iconSize: 12,
    },
    medium: {
        size: 14,
        lineHeight: "16px",
        iconSize: 14,
    },
    large: {
        size: 16,
        lineHeight: "19px",
        iconSize: 16,
    },
};

export const createLinkIconStyles = ({
    icon,
    linkStyles = {},
    sizeVariant = "small",
}: {
    icon?: React.ComponentType | string;
    linkStyles?: LinkPresentationProps;
    sizeVariant?: "small" | "medium" | "large";
}) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: linkSizeVariants[sizeVariant].size,
                    lineHeight: linkSizeVariants[sizeVariant].lineHeight,
                    weight: fontConfig.weight.medium,
                    css: css`
                        letter-spacing: 0;
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: linkSizeVariants[sizeVariant].iconSize,
                        css: css`
                            ${marginPosition}: 6px;
                        `,
                    },
                    position: linkStyles?.icon?.position,
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const sharedProductDescriptionStyles = ({
    lines = 2,
    sm,
    includeMinHeight = true,
    lineHeight = 17,
    fontSize = 14,
    styles = {},
}: ProductDescriptionOptions = {}): ProductDescriptionStyles => {
    let smLines = sm;
    if (!smLines) {
        smLines = lines;
    }
    const minHeightCss = includeMinHeight
        ? css`
              min-height: ${lines * lineHeight}px;
              ${xScreen("lte", "sm")} {
                  min-height: ${smLines * lineHeight}px;
              }
          `
        : "";

    return mergeWithCss<ProductDescriptionStyles, ProductDescriptionStyles>(
        {
            productDetailDescription: {
                size: fontSize,
                color: "text",
                weight: fontConfig.weight.medium,
                css: css`
                    display: block;
                    letter-spacing: 0;
                    line-height: ${lineHeight}px;
                    ${ellipsis(lines)}
                    ${xScreen("lte", "sm")} {
                        ${ellipsis(smLines)}
                    }
                    ${minHeightCss}
                    @media print {
                        display: block;
                        width: 100%;
                        text-align: start;
                    }
                    margin-bottom: 0;
                `,
            },
            productDetailLink: {
                color: "text",
                typographyProps: {
                    size: fontSize,
                    color: "text",
                    weight: fontConfig.weight.medium,
                    lineHeight: `${lineHeight}px`,
                    css: css`
                        letter-spacing: 0;
                        ${ellipsis(lines)}
                        ${xScreen("lte", "sm")} {
                            ${ellipsis(smLines)}
                        }
                    `,
                },
                css: css`
                    align-items: flex-start;
                    ${xScreen("lte", "sm")} {
                        ${ellipsis(smLines)}
                    }
                    ${minHeightCss}
                    @media print {
                        display: inline-block;
                        width: 100%;
                        text-align: start;
                    }
                `,
            },
        },
        styles,
    );
};

export const customBadgesStyles = {
    container: {
        css: css`
            display: flex;
            gap: 4px;
            flex-wrap: wrap;

            [role="button"] {
                padding: 1px;
                width: 20px;
                height: 20px;
                background-color: ${color("common.white")};
                display: flex;
                margin-left: 0;
                margin-right: 0;
                > div {
                    display: flex;
                }
            }

            img {
                max-width: 100%;
                max-height: 100%;
            }

            .popup-content-wrapper {
                > div {
                    white-space: initial;
                }
            }
        `,
    },
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
            gap: 10px;
            width: 100%;
            margin-top: 32px;
            &.divider {
                margin-top: 20px;
                padding-top: 20px;
                border-top: 1px solid ${color("common.border")};
            }
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    submitButton: {
        variant: "primary",
    },
};
